import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';

import belowIcon from '../../asset/icon/below.png';
import defaultImg from '../../asset/project/default_1.png';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { BULK_UPLOAD_IMAGE_MAX_COUNT } from '../../shared/constants/limits';
import { convertWorkSizeToString, WorkSimpleWithDetails } from '../../shared/dto/EntityRepository';
import { AlertPosition, AlertType, ProjectElementType, usesWorkDuration, WorkContentMode } from '../../shared/enum/EnumRepository';
import { useProjectElementState } from '../../shared/hooks/useApi/useProjectElement';
import { useWorkDetailActions } from '../../shared/hooks/useApi/useWorkDetail';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import { useImportStationWorkStore } from '../../shared/store/importStationWorkStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useSettingStore } from '../../shared/store/settingStore';
import { resolveUploadFileOriginUrl } from '../../shared/util/normalizeWorkData';
import { resolveWorkArtistName, shouldShowWorkArtist } from '../../shared/util/workArtist';
import { shrinkString } from '../../shared/util/shrinkString';

import BlockWithVisible from './BlockWithVisible';
import SimpleWorkDetailForPE from './SimpleWorkDetailForPE';

interface SimpleWorkForPEProps {
  data: WorkSimpleWithDetails;
}

const SimpleWorkForPE: React.FC<SimpleWorkForPEProps> = ({ data }) => {
  const { getSimpleWorkDetailList } = useWorkDetailActions();
  const { addSelectedData, selectedDataList } = useImportStationWorkStore();
  const { projectElementList } = useProjectElementState();
  const { enqueueAlert } = useStandardAlertStore();
  const setting = useSettingStore((s) => s.setting);
  const showArtist = shouldShowWorkArtist(setting);
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const imageUrl = useOptimizedImageUrl(resolveUploadFileOriginUrl(data.uploadFile), imgContainerRef);

  const workIdsInProject = useMemo(
    () =>
      projectElementList
        .filter((pe) => pe.projectElementType === ProjectElementType.WORK)
        .map((pe) => (pe as { work: { id: string } }).work.id),
    [projectElementList]
  );
  const isAlreadyInProject = workIdsInProject.includes(data.id);
  const isSelected = selectedDataList.some((s) => s.workId === data.id && s.detailId === undefined);

  const handleRowClick = () => {
    if (isAlreadyInProject) return;
    if (selectedDataList.length >= BULK_UPLOAD_IMAGE_MAX_COUNT) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('BULK_IMPORT_MAX_EXCEEDED', { maxCount: BULK_UPLOAD_IMAGE_MAX_COUNT }),
      });
      return;
    }
    addSelectedData({
      workId: data.id,
      originUrl: resolveUploadFileOriginUrl(data.uploadFile),
      title: data.title,
      prodYear: data.prodYear,
      material: data.material,
      size: data.size,
      sizeText: data.sizeText,
      dimensionsType: data.dimensionsType,
      duration: data.duration,
      artist: data.artist,
    });
  };

  const shrinkedTitle = useMemo(() => shrinkString(data.title, 24), [data.title]);
  const artistName = showArtist
    ? resolveWorkArtistName(data.artist, WorkContentMode.Orig)
    : '';
  const shrinkedArtist = useMemo(
    () => (artistName ? shrinkString(artistName, 16) : '-'),
    [artistName],
  );
  const sizeString = useMemo(() => {
    if (usesWorkDuration(data.workType)) {
      return data.duration || '-';
    }
    return convertWorkSizeToString(data.size, data.dimensionsType, data.sizeText, 'Variable');
  }, [data.size, data.sizeText, data.dimensionsType, data.workType, data.duration]);

  const onClickDetailHandler = async (workId: string) => {
    await getSimpleWorkDetailList(workId);
  };

  return (
    <>
    <SimpleWorkForPEComp
      key={data.id}
      $isSelected={isSelected}
      $isAlreadyInProject={isAlreadyInProject}
      onClick={handleRowClick}
    >
      <ImageBlock>
        <ImgWrapper ref={imgContainerRef}>
          <WorkImage src={imageUrl === '' ? defaultImg : imageUrl} alt={data.title} />
        </ImgWrapper>
      </ImageBlock>
      {showArtist && (
        <ArtistBlock>
          <span>{shrinkedArtist}</span>
        </ArtistBlock>
      )}
      <TitleBlock>
        <span>{shrinkedTitle}</span>
      </TitleBlock>
      <BlockWithVisible
          width={"0.6"}
          value={data.prodYear || '-'}/>
      <BlockWithVisible
          width={"1"}
          value={sizeString}/>
      <BtnWrapper $isEnabled={(data.hasDetail && data.detailList.length === 0)} onClick={(e) => { e.stopPropagation(); data.hasDetail && onClickDetailHandler(data.id); }}>
        {(data.hasDetail && data.detailList.length === 0) ? (
          <UniversalIconBtn
            size="small"
            border={false}
            isTransparent={true}
            backgroundImg={belowIcon}
            onClick={(e) => {
              e.stopPropagation();
              onClickDetailHandler(data.id);
            }}
            aria-label="Show work details"
          />
        ) : '-'}
      </BtnWrapper>
    </SimpleWorkForPEComp>
    <SimpleWorkForPEDetailContainer $detailCount={data.detailList.length}>  
      {data.detailList.map((detail) => (
        <SimpleWorkDetailForPE
          key={detail.id}
          workId={data.id}
          data={detail}
          showArtist={showArtist}
          artistName={shrinkedArtist}
        />
      ))}
    </SimpleWorkForPEDetailContainer>
    </>
  );
};

const SimpleWorkForPEComp = styled.div<{ $isSelected?: boolean; $isAlreadyInProject?: boolean }>`
  width: 100%;
  height: fit-content;
  min-height: 50px;
  max-height: 70px;
  position: relative;
  display: flex;
  align-items: center;
  transition: all 0.3s ease-in-out;
  opacity: ${({ $isSelected, $isAlreadyInProject }) => ($isAlreadyInProject ? 0.4 : $isSelected ? 0.5 : 1)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};

  cursor: ${({ $isSelected, $isAlreadyInProject }) => ($isAlreadyInProject || $isSelected ? 'not-allowed' : 'pointer')};
`

const ImageBlock = styled.div`
  height: 100%;
  flex: 0.8;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;
const ImgWrapper = styled.div`
  position: relative;
  width: 60%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`
const WorkImage = styled.img`
  //부모 크기에 맞춤
  width: 100%;
  height: 100%; 
  object-fit: contain;
`;

const ArtistBlock = styled.div`
  flex: 1;
  min-width: 0;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 4px;
  ${({ theme }) => theme.typography.Body_04};

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const TitleBlock = styled.div`
  flex: 2;
  min-width: 0;

  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.typography.Body_04};

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const BtnWrapper = styled.div<{ $isEnabled?: boolean }>`
  flex: 0.2;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $isEnabled }) => ($isEnabled ? 'pointer' : 'default')};
  ${({ theme }) => theme.typography.Body_04};
`


const SimpleWorkForPEDetailContainer = styled.div<{ $detailCount: number }>`
  width: 100%;
  height: ${({ $detailCount }) => $detailCount * 50}px;
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: height 0.8s ease-in-out;
`;
export default SimpleWorkForPE;
